import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, InputAdornment
} from "@mui/material";
import dayjs from "dayjs";
import { CreateInvoiceRequest } from "../../entities/invoice/api/create-invoice";
import { UseCreateInvoiceRequest, useCreateInvoice } from "../../features/invoice/create-invoice/api/useCreateInvoice";
import { createInvoiceReq } from "../../entities/invoice/api/create-invoice";
import { toast } from "react-toastify";
import { extractPercentage } from "../../shared/lib/numbers";
import { calculateServiceOrderLinesBillingTotal } from "../../entities/service-order-line/model/service-order-line";

interface LineInvoice {
  line_id: string;
  pericia_id: string | null;
  car_id: string | null;
  date: string;
  cost: number;
}

interface InvoiceModalProps {
  open: boolean;
  onClose: () => void;
  costumerId: string;
  lines: LineInvoice[];
  onSuccess: () => void;
}

const INITIAL_INVOICE: UseCreateInvoiceRequest = {
  costumerId: "",
  totalPericias: 0,
  totalPrice: 0,
  startDate: new Date(),
  endDate: new Date(),
  agentComission: 0,
  pericias: [],
  lines: undefined
};

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  open,
  onClose,
  costumerId,
  lines,
  onSuccess,
}) => {
  const [agentComission, setAgentComission] = useState(0);
  const [invoice, setInvoice] = useState<UseCreateInvoiceRequest>(INITIAL_INVOICE);

  // Calculate total price from lines
  const totalPrice = calculateServiceOrderLinesBillingTotal(lines);

  // Calculate start and end dates from lines
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalLines, setTotalLines] = useState(lines.length);

  const {
    data: createInvoiceRes,
    error: errorCreatingInvoice,
    isLoading: isLoadingCreateInvoice
  } = useCreateInvoice(createInvoiceReq, invoice);

  useEffect(() => {
    if (lines.length > 0) {
      const sorted = [...lines].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
      setStartDate(dayjs(sorted[0].date).format("YYYY-MM-DD"));
      setEndDate(dayjs(sorted[sorted.length - 1].date).format("YYYY-MM-DD"));
      setTotalLines(lines.length);
    } else {
      setStartDate("");
      setEndDate("");
      setTotalLines(0);
    }
  }, [lines]);

  useEffect(() => {
    if (!createInvoiceRes) return;

    if (errorCreatingInvoice) {
      toast.error(errorCreatingInvoice.toast_message || "Erro ao criar fatura.");
      return;
    }

    toast.success("Fatura criada com sucesso!");
    onClose();
    onSuccess();
  }, [createInvoiceRes, errorCreatingInvoice]);

  const handleConfirm = () => {
    const periciaLines = lines.filter(line => line.pericia_id);

     const pericias = periciaLines.map(line => ({
      id: line.pericia_id!,
      car_id: line.car_id!
    }));

    const linesArray = lines.map(line => line.line_id);

    const invoiceRequest: UseCreateInvoiceRequest = {
      costumerId,
      totalPericias: periciaLines.length,
      totalPrice,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      agentComission,
      pericias,
      lines: linesArray.length > 0 ? linesArray : undefined
    };
    
    setInvoice(invoiceRequest);
  };

  // Format for display
  const startDateDisplay = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
  const endDateDisplay = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gerar Fatura</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Total de Linhas"
            type="number"
            fullWidth
            value={totalLines}
            disabled
          />
          <TextField
            label="Valor Total"
            type="number"
            fullWidth
            value={totalPrice.toFixed(2)}
            disabled
            InputProps={{
              startAdornment: <InputAdornment position="start">CHF</InputAdornment>,
            }}
          />
          <TextField
            label="Data Inicial"
            fullWidth
            value={startDateDisplay}
            disabled
          />
          <TextField
            label="Data Final"
            fullWidth
            value={endDateDisplay}
            disabled
          />
          <TextField
            label="Comissão do Agente"
            type="number"
            fullWidth
            value={agentComission}
            onChange={e => setAgentComission(extractPercentage(e.target.value) as unknown as number)}
            InputProps={{
              startAdornment: <InputAdornment position="start">(%)</InputAdornment>,
            }}
            inputProps={{ min: 0 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoadingCreateInvoice}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoadingCreateInvoice || lines.length === 0 || !costumerId}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;
